import { useState, useEffect } from 'react';
import API from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Users, Calendar, IndianRupee, TrendingUp, Activity, BarChart3, UserCheck, UserX, RefreshCw, Download, FileText, Database, Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#0f766e', '#f59e0b', '#3b82f6', '#10b981', '#ef4444', '#8b5cf6'];

export default function AnalyticsPage() {
  const [dashboard, setDashboard] = useState(null);
  const [revenue, setRevenue] = useState([]);
  const [diagnoses, setDiagnoses] = useState([]);
  const [patientStats, setPatientStats] = useState(null);
  const [apptStats, setApptStats] = useState(null);
  const [retentionStats, setRetentionStats] = useState(null);
  const [marketplaceStats, setMarketplaceStats] = useState(null);
  const [mrStats, setMrStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState('');

  useEffect(() => {
    Promise.all([
      API.get('/analytics/dashboard'),
      API.get('/analytics/revenue-trend'),
      API.get('/analytics/top-diagnoses'),
      API.get('/analytics/patient-stats'),
      API.get('/analytics/appointment-stats'),
      API.get('/analytics/retention-stats'),
      API.get('/analytics/marketplace').catch(() => ({ data: null })),
      API.get('/analytics/mr-visits').catch(() => ({ data: null })),
    ]).then(([d, r, dg, ps, as_, rs, mp, mr]) => {
      setDashboard(d.data);
      setRevenue(r.data.reverse());
      setDiagnoses(dg.data);
      setPatientStats(ps.data);
      setApptStats(as_.data);
      setRetentionStats(rs.data);
      setMarketplaceStats(mp.data);
      setMrStats(mr.data);
    }).catch(console.error)
    .finally(() => setLoading(false));
  }, []);

  const downloadDatabaseExport = async () => {
    setExporting('database');
    try {
      const response = await fetch(`${BACKEND_URL}/api/reports/full-database-export`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('clinic_token')}` }
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `clinic_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('Database exported successfully');
    } catch (err) { toast.error('Export failed'); }
    finally { setExporting(''); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-6 h-6 border-2 border-teal-700 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const genderData = patientStats?.gender_distribution
    ? Object.entries(patientStats.gender_distribution).map(([name, value]) => ({ name, value }))
    : [];

  const apptStatusData = apptStats
    ? Object.entries(apptStats).map(([name, value]) => ({ name, value }))
    : [];

  return (
    <div data-testid="analytics-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{fontFamily:'Manrope'}}>Analytics</h1>
          <p className="text-sm text-slate-500">Practice insights and performance metrics</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 stagger-children">
        {[
          { label: 'Total Patients', value: dashboard?.total_patients || 0, icon: Users, color: 'text-teal-600 bg-teal-50' },
          { label: "Today's Appointments", value: dashboard?.today_appointments || 0, icon: Calendar, color: 'text-blue-600 bg-blue-50' },
          { label: 'Monthly Appointments', value: dashboard?.monthly_appointments || 0, icon: Activity, color: 'text-purple-600 bg-purple-50' },
          { label: "Today's Revenue", value: `\u20B9${(dashboard?.daily_revenue || 0).toLocaleString('en-IN')}`, icon: IndianRupee, color: 'text-emerald-600 bg-emerald-50' },
          { label: 'Monthly Revenue', value: `\u20B9${(dashboard?.monthly_revenue || 0).toLocaleString('en-IN')}`, icon: TrendingUp, color: 'text-amber-600 bg-amber-50' },
        ].map(stat => (
          <Card key={stat.label} className="border-slate-200 dark:border-slate-800">
            <CardContent className="p-4">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${stat.color}`}>
                <stat.icon className="w-4 h-4" />
              </div>
              <p className="text-xl font-bold text-slate-900 dark:text-white">{stat.value}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Key Performance Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <Card className="border-slate-200 dark:border-slate-800 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/10 dark:to-slate-900">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Completion Rate</p>
                <p className="text-2xl font-bold text-emerald-600" data-testid="completion-rate">{retentionStats?.completion_rate || 0}%</p>
              </div>
              <UserCheck className="w-8 h-8 text-emerald-400" />
            </div>
            <p className="text-[10px] text-slate-400 mt-1">{retentionStats?.completed_month || 0} completed this month</p>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200 dark:border-slate-800 bg-gradient-to-br from-red-50 to-white dark:from-red-900/10 dark:to-slate-900">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">No-Show Rate</p>
                <p className="text-2xl font-bold text-red-600" data-testid="noshow-rate">{retentionStats?.no_show_rate || 0}%</p>
              </div>
              <UserX className="w-8 h-8 text-red-400" />
            </div>
            <p className="text-[10px] text-slate-400 mt-1">{retentionStats?.no_shows_month || 0} no-shows this month</p>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200 dark:border-slate-800 bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/10 dark:to-slate-900">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Patient Retention</p>
                <p className="text-2xl font-bold text-blue-600" data-testid="retention-rate">{retentionStats?.retention_rate || 0}%</p>
              </div>
              <RefreshCw className="w-8 h-8 text-blue-400" />
            </div>
            <p className="text-[10px] text-slate-400 mt-1">{retentionStats?.returning_patients || 0} returning patients</p>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200 dark:border-slate-800 bg-gradient-to-br from-purple-50 to-white dark:from-purple-900/10 dark:to-slate-900">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Avg Visits/Patient</p>
                <p className="text-2xl font-bold text-purple-600" data-testid="avg-visits">{retentionStats?.avg_visits_per_patient || 0}</p>
              </div>
              <Activity className="w-8 h-8 text-purple-400" />
            </div>
            <p className="text-[10px] text-slate-400 mt-1">Average visits per patient</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Revenue Trend */}
        <Card className="border-slate-200 dark:border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2" style={{fontFamily:'Manrope'}}>
              <BarChart3 className="w-5 h-5 text-teal-600" /> Revenue Trend
            </CardTitle>
          </CardHeader>
          <CardContent data-testid="analytics-revenue-chart">
            {revenue.length === 0 ? (
              <div className="text-center py-12 text-slate-400 text-sm">No revenue data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={revenue}>
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={v => v.slice(5)} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `\u20B9${v}`} />
                  <Tooltip
                    formatter={(value) => [`\u20B9${value.toLocaleString('en-IN')}`, 'Revenue']}
                    labelStyle={{ fontSize: 12 }}
                  />
                  <Bar dataKey="revenue" fill="#0f766e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Top Diagnoses */}
        <Card className="border-slate-200 dark:border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg" style={{fontFamily:'Manrope'}}>Top Diagnoses</CardTitle>
          </CardHeader>
          <CardContent data-testid="analytics-diagnoses-chart">
            {diagnoses.length === 0 ? (
              <div className="text-center py-12 text-slate-400 text-sm">No diagnosis data yet</div>
            ) : (
              <div className="space-y-3">
                {diagnoses.map((d, i) => {
                  const max = diagnoses[0]?.count || 1;
                  return (
                    <div key={d.diagnosis} className="flex items-center gap-3">
                      <span className="text-xs text-slate-500 w-32 truncate">{d.diagnosis}</span>
                      <div className="flex-1 h-6 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${(d.count / max) * 100}%`, backgroundColor: COLORS[i % COLORS.length] }}
                        />
                      </div>
                      <span className="text-sm font-bold text-slate-700 dark:text-slate-300 w-8 text-right">{d.count}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Gender Distribution */}
        <Card className="border-slate-200 dark:border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg" style={{fontFamily:'Manrope'}}>Patient Demographics</CardTitle>
          </CardHeader>
          <CardContent>
            {genderData.length === 0 ? (
              <div className="text-center py-12 text-slate-400 text-sm">No patient data yet</div>
            ) : (
              <div className="flex items-center justify-center gap-8">
                <ResponsiveContainer width={160} height={160}>
                  <PieChart>
                    <Pie
                      data={genderData}
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={70}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {genderData.map((_, i) => (
                        <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2">
                  {genderData.map((g, i) => (
                    <div key={g.name} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                      <span className="text-sm text-slate-600 dark:text-slate-400">{g.name}: <strong>{g.value}</strong></span>
                    </div>
                  ))}
                  <div className="pt-1 border-t border-slate-100 dark:border-slate-800">
                    <span className="text-xs text-slate-400">New this month: <strong>{patientStats?.new_this_month || 0}</strong></span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Appointment Status */}
        <Card className="border-slate-200 dark:border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg" style={{fontFamily:'Manrope'}}>Appointment Status (This Month)</CardTitle>
          </CardHeader>
          <CardContent>
            {apptStatusData.length === 0 ? (
              <div className="text-center py-12 text-slate-400 text-sm">No appointment data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={apptStatusData} layout="vertical">
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={80} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {apptStatusData.map((entry, i) => (
                      <Cell key={`cell-${i}`} fill={
                        entry.name === 'completed' ? '#10b981' :
                        entry.name === 'waiting' ? '#f59e0b' :
                        entry.name === 'in-progress' ? '#3b82f6' :
                        entry.name === 'no-show' ? '#ef4444' : '#94a3b8'
                      } />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Marketplace Analytics Section */}
      {marketplaceStats && (
        <Card className="border-slate-200 dark:border-slate-800">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{fontFamily:'Manrope'}}>
              <BarChart3 className="w-5 h-5 text-purple-600" /> Marketplace Analytics
            </CardTitle>
            <CardDescription>B2B orders and vendor analytics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-center">
                <p className="text-3xl font-bold text-purple-700">{marketplaceStats.total_orders || 0}</p>
                <p className="text-sm text-slate-500">Total Orders</p>
              </div>
              <div className="p-4 bg-teal-50 dark:bg-teal-900/20 rounded-lg text-center">
                <p className="text-3xl font-bold text-teal-700">{marketplaceStats.month_orders || 0}</p>
                <p className="text-sm text-slate-500">This Month</p>
              </div>
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-center">
                <p className="text-3xl font-bold text-amber-700">₹{(marketplaceStats.total_spend || 0).toLocaleString()}</p>
                <p className="text-sm text-slate-500">Total B2B Spend</p>
              </div>
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                <p className="text-3xl font-bold text-blue-700">₹{(marketplaceStats.month_spend || 0).toLocaleString()}</p>
                <p className="text-sm text-slate-500">Month Spend</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Vendor Type Breakdown */}
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-3">Orders by Vendor Type</h4>
                {marketplaceStats.vendor_type_breakdown?.length > 0 ? (
                  <div className="space-y-2">
                    {marketplaceStats.vendor_type_breakdown.map((v, i) => (
                      <div key={`el-${i}`} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                          <span className="capitalize text-sm">{v.type || 'Unknown'}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-medium">{v.count}</span>
                          <span className="text-xs text-slate-400 ml-2">₹{v.total?.toLocaleString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">No orders yet</p>
                )}
              </div>
              
              {/* Top Vendors */}
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-3">Top Vendors</h4>
                {marketplaceStats.top_vendors?.length > 0 ? (
                  <div className="space-y-2">
                    {marketplaceStats.top_vendors.map((v, i) => (
                      <div key={`el-${i}`} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                        <span className="text-sm truncate flex-1">{v.vendor_name}</span>
                        <div className="text-right">
                          <Badge variant="secondary">{v.orders} orders</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">No vendors yet</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* MR Visits Analytics */}
      {mrStats && (
        <Card className="border-slate-200 dark:border-slate-800">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{fontFamily:'Manrope'}}>
              <Users className="w-5 h-5 text-indigo-600" /> MR Visitor Analytics
            </CardTitle>
            <CardDescription>Medical representative visit tracking</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg text-center">
                <p className="text-3xl font-bold text-indigo-700">{mrStats.today_visits || 0}</p>
                <p className="text-sm text-slate-500">Today</p>
              </div>
              <div className="p-4 bg-violet-50 dark:bg-violet-900/20 rounded-lg text-center">
                <p className="text-3xl font-bold text-violet-700">{mrStats.month_visits || 0}</p>
                <p className="text-sm text-slate-500">This Month</p>
              </div>
              <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg text-center">
                <p className="text-3xl font-bold text-slate-700">{mrStats.total_visits || 0}</p>
                <p className="text-sm text-slate-500">Total</p>
              </div>
            </div>
            
            {mrStats.top_companies?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-3">Top Companies This Month</h4>
                <div className="flex flex-wrap gap-2">
                  {mrStats.top_companies.map((c, i) => (
                    <Badge key={`badge-${i}`} variant="outline" className="py-1.5">
                      {c.company} <span className="ml-1 text-teal-600">({c.visits})</span>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Data Export Section */}
      <Card className="border-slate-200 dark:border-slate-800">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{fontFamily:'Manrope'}}>
            <Download className="w-5 h-5 text-teal-600" /> Data Export
          </CardTitle>
          <CardDescription>Export all clinic data for backup or compliance purposes</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <Database className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-sm">Full Database Export</p>
                  <p className="text-xs text-slate-400">All patients, visits, invoices, and settings</p>
                </div>
              </div>
              <Button 
                onClick={downloadDatabaseExport} 
                disabled={exporting === 'database'}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="export-database-btn"
              >
                {exporting === 'database' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                {exporting === 'database' ? 'Exporting...' : 'Download JSON Backup'}
              </Button>
            </div>
            
            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <FileText className="w-5 h-5 text-purple-600" />
                <div>
                  <p className="font-medium text-sm">Patient Record PDFs</p>
                  <p className="text-xs text-slate-400">Generate from Patients page or History page</p>
                </div>
              </div>
              <Button 
                variant="outline"
                className="w-full"
                onClick={() => window.location.href = '/patient-history'}
              >
                <FileText className="w-4 h-4 mr-2" /> Go to Patient History
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
